import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Grid,
  Snackbar,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormHelperText,
} from "@mui/material";
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import { useFormik } from "formik";
import * as yup from "yup";
import RuleTargetSelector from "./RuleTargetSelector";
import MccListSelector from "./MccListSelector";
import UserService from "../services/UserService";
import { LETTER_NUMBERS_SPACES_HYPHENS_AND_UNDERSCORES } from "./constants/validationConstans";
import Labels from "../labels/Labels";

export default function AddSpendRule(props) {
  const queryClient = useQueryClient();

  const createSpendRuleMutation = useMutation(
    (values) => {
      return HttpService.getAxiosClient().post(
        window.API_URL + `/rules/spendrules/create`,
        values
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("spendRules");
        setFeedback({
          open: true,
          type: "success",

          message: Labels.getLabel(
            "spendRule.createSuccess",
            "Spend rule created successfully"
          ),
          onClose: () => {
            setFeedback({ open: false });
            props.handleClose();
          },
        });
      },
      onError: (error) => {
        setFeedback({
          open: true,
          type: "error",
          message: Labels.getLabel(
            "spendRule.createError",
            "Error creating the spend rule"
          ),
        });
      },
    }
  );

  const handleFeedbackClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedback({ open: false, type: "error", message: "" });
  };

  // First update the validation schema
  const validationSchema = yup.object({
    companiesList: yup
      .object()
      .nullable()
      .required(
        Labels.getLabel("spendRule.companyRequired", "Company is required")
      ), // Changed from array to object
    ruleName: yup
      .string("Enter the rule name")
      .required(
        Labels.getLabel("spendRule.ruleNameRequired", "Rule name is required")
      )
      .min(
        4,
        Labels.getLabel(
          "spendRule.ruleNameMin",
          "Rule name must be at least 4 characters"
        )
      )
      .max(
        250,
        Labels.getLabel(
          "spendRule.ruleNameMax",
          "Rule name must not exceed 250 characters"
        )
      )
      .matches(
        LETTER_NUMBERS_SPACES_HYPHENS_AND_UNDERSCORES,
        Labels.getLabel(
          "spendRule.ruleNameFormat",
          "Rule name can only contain letters, numbers, spaces, hyphens, underscores"
        )
      ),
    ruleAccessType: yup
      .string("Select the access type")
      .required(
        Labels.getLabel(
          "spendRule.accessTypeRequired",
          "Access type is required"
        )
      ),
    ruleTargetType: yup
      .string("Select the target type")
      .required(
        Labels.getLabel(
          "spendRule.targetTypeRequired",
          "Target type is required"
        )
      ),
    ruleTargetProduct: yup
      .object()
      .nullable()
      .required(
        Labels.getLabel("spendRule.targetRequired", "Target is required")
      ),
    mccList: yup
      .array()
      .min(
        1,
        Labels.getLabel(
          "spendRule.mccRequired",
          "At least one MCC code is required"
        )
      )
      .required(
        Labels.getLabel("spendRule.mccListRequired", "MCC list is required")
      ),
  });

  const formik = useFormik({
    initialValues: {
      companiesList: null,
      ruleName: "",
      ruleAccessType: "BLOCK",
      ruleTargetType: "PROGRAM",
      ruleTargetProduct: null,
      mccList: [],
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      // Transform companiesList to only include idEmployer values
      // Transform mccList to only include codes before submitting
      const transformedValues = {
        ...values,
        employerName: values.companiesList?.description || "",
        companiesList: values.companiesList?.idEmployer || null, // Extract just the idEmployer
        // companiesList: values.companiesList.map(company => company.idEmployer),
        //mccList: values.mccList.map(mcc => mcc.code)
        ruleTargetID: values.ruleTargetProduct?.idCardProduct || null,
        ruleTargetName: values.ruleTargetProduct?.name || null,
      };

      delete transformedValues.ruleTargetProduct;

      createSpendRuleMutation.mutate(transformedValues);
    },
  });

  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  // Fetch company data
  async function fetchCompanyData() {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL + `/employer/all`
    );
    return response;
  }

  const { data: companyData, isLoading: isLoadingCompanies } = useQuery(
    ["companies"],
    fetchCompanyData
  );

  const employerID = UserService.getTokenClaim("employer_id");

  function getTransformedCompanyList(companyList) {
    // if ("ANY" === sessionStorage.getItem('chosenEmployerId')) {
    //   return companyList;
    // }
    //
    // let transformedCompanyList = [];
    // let employersBeingOverseen = [];
    // for (const company of companyList) {
    //   if (Array.isArray(company.employersBeingOverseen) && company.employersBeingOverseen.length > 0) {
    //     employersBeingOverseen.push(...company.employersBeingOverseen);
    //   }
    // }
    //
    // transformedCompanyList = companyList.concat(employersBeingOverseen);
    // return transformedCompanyList;

    if ("ANY" === sessionStorage.getItem("chosenEmployerId")) {
      return companyList;
    }

    const employersBeingOverseen = companyList.flatMap(
      (company) => company.employersBeingOverseen || []
    );

    return [...companyList, ...employersBeingOverseen];
  }

  // For programs, users, or groups based on target type
  async function fetchTargetOptions() {
    // This would typically fetch different options based on the selected target type
    // For now, we'll return a mock response
    return { data: [] };
  }

  const { data: targetOptions, isLoading: isLoadingTargets } = useQuery(
    ["targetOptions", formik.values.ruleTargetType],
    () => fetchTargetOptions(),
    {
      enabled: !!formik.values.ruleTargetType,
    }
  );

  return (
    <div>
      <Box
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={formik.handleSubmit}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {/*Then modify the Autocomplete component*/}

            <Autocomplete
              fullWidth
              id="companiesList"
              options={
                !isLoadingCompanies
                  ? Array.isArray(companyData?.data)
                    ? getTransformedCompanyList(companyData?.data, employerID)
                    : []
                  : []
              }
              getOptionLabel={(option) =>
                `${option.description}-[${option.idEmployer}]`
              }
              filterSelectedOptions
              value={formik.values.companiesList}
              onChange={(event, selectedOption) => {
                formik.setFieldValue("companiesList", selectedOption);
                formik.setFieldValue("ruleTargetProduct", null);
                formik.setFieldValue("mccList", []);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={
                    formik.touched.companiesList &&
                    Boolean(formik.errors.companiesList)
                  }
                  helperText={
                    formik.touched.companiesList && formik.errors.companiesList
                  }
                  label={Labels.getLabel("spendRule.company", "Company")}
                  placeholder={Labels.getLabel(
                    "spendRule.selectCompany",
                    "Select a company"
                  )}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              id="ruleName"
              name="ruleName"
              label={Labels.getLabel("spendRule.ruleName", "Rule Name")}
              value={formik.values.ruleName}
              onChange={formik.handleChange}
              error={formik.touched.ruleName && Boolean(formik.errors.ruleName)}
              helperText={formik.touched.ruleName && formik.errors.ruleName}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl
              fullWidth
              error={
                formik.touched.ruleAccessType &&
                Boolean(formik.errors.ruleAccessType)
              }
            >
              <InputLabel id="ruleAccessType-label">
                {Labels.getLabel("spendRule.accessType", "Access Type")}
              </InputLabel>
              <Select
                labelId="ruleAccessType-label"
                id="ruleAccessType"
                name="ruleAccessType"
                value={formik.values.ruleAccessType}
                label={Labels.getLabel("spendRule.accessType", "Access Type")}
                onChange={formik.handleChange}
              >
                <MenuItem value="BLOCK">
                  {Labels.getLabel("spendRule.block", "BLOCK")}
                </MenuItem>
                <MenuItem value="ALLOW">
                  {Labels.getLabel("spendRule.allow", "ALLOW")}
                </MenuItem>
              </Select>
              {formik.touched.ruleAccessType &&
                formik.errors.ruleAccessType && (
                  <FormHelperText>
                    {formik.errors.ruleAccessType}
                  </FormHelperText>
                )}
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl
              fullWidth
              error={
                formik.touched.ruleTargetType &&
                Boolean(formik.errors.ruleTargetType)
              }
            >
              <InputLabel id="ruleTargetType-label">
                {Labels.getLabel("spendRule.targetType", "Target Type")}
              </InputLabel>
              <Select
                labelId="ruleTargetType-label"
                id="ruleTargetType"
                name="ruleTargetType"
                value={formik.values.ruleTargetType}
                disabled={true}
                label={Labels.getLabel("spendRule.targetType", "Target Type")}
                onChange={formik.handleChange}
              >
                <MenuItem value="PROGRAM">
                  {Labels.getLabel("spendRule.program", "PROGRAM")}
                </MenuItem>
              </Select>
              {formik.touched.ruleTargetType &&
                formik.errors.ruleTargetType && (
                  <FormHelperText>
                    {formik.errors.ruleTargetType}
                  </FormHelperText>
                )}
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <RuleTargetSelector formik={formik} />
          </Grid>
          <Grid item xs={12}>
            <MccListSelector formik={formik} />
          </Grid>
          <Grid item xs={12} pt={3}>
            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={createSpendRuleMutation.isLoading}
            >
              {Labels.getLabel("spendRule.save", "SAVE")}
            </Button>
          </Grid>
        </Grid>
      </Box>
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={feedback.onClose ? feedback.onClose : handleFeedbackClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={feedback.onClose ? feedback.onClose : handleFeedbackClose}
          severity={feedback.type}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
